// Project Tracker: the .json file used for Export and Import (Step 3 of Section 30).
// No imports here, so it can be tested on its own. Import never deletes anything.

export const FORMAT = "adyantam-project-tracker";
export const VERSION = 1;
const OPEN_STATUSES = ["Pending", "Doing", "Check", "Parked"];
export const RELEASES = ["Before pilot", "After pilot", "Later"];
export const PRIORITIES = ["High", "Medium", "Low"];
// Words shown on screen (the saved values stay Pending, Doing, Parked, Done).
const SHOW = { Pending: "To do", Doing: "In progress", Check: "Needs check", Parked: "On hold", Done: "Done" };
const show = (v) => SHOW[v] || v;
const FIELDS = ["title", "project", "type", "status", "link", "note", "details", "release", "priority", "doneWhen", "targetDate"];
const IGNORED = ["key", "num", "addHistory", "history", "id", "createdBy", "createdByUid", "createdAt", "updatedAt", "closedAt"];
const LIMITS = { title: 200, doneWhen: 300, link: 200, note: 10000, details: 20000, line: 10000 };
// Same words as Rule 6 of Start Here. Only a warning: the Owner decides.
const SECRET_WORDS = /secret|passw|token|private_key|apikey/i;

const ABOUT =
  "Project Tracker file. To send changes back: keep each task's key and change only the fields you want to change; " +
  "a task without a key becomes a new task; addHistory is a list of short lines added to the History; " +
  "the import never deletes anything. Never put a password, key or token in this file.";

function exportTask(t) {
  return {
    key: t.id,
    num: t.num ? "T-" + String(t.num).padStart(3, "0") : "",
    title: t.title || "",
    project: t.project || "",
    type: t.type || "",
    status: t.status || "",
    release: t.release || "", priority: t.priority || "", targetDate: t.targetDate || "", doneWhen: t.doneWhen || "",
    link: t.link || "",
    note: t.note || "",
    details: t.details || "",
    history: (Array.isArray(t.history) ? t.history : []).slice(-5),
    createdBy: t.createdBy || "",
  };
}

// Only the open tasks (Pending, Doing, Parked).
export function buildExportFile(tasks, nowIso) {
  return {
    format: FORMAT,
    version: VERSION,
    exportedAt: nowIso,
    about: ABOUT,
    tasks: tasks.filter((t) => OPEN_STATUSES.includes(t.status)).map(exportTask),
  };
}

// The tasks shown on the screen right now (any status). Used by "Export N shown".
// Same file layout as the Export button (buildProjectsExport): lock, summary, summary table, full History,
// open tasks under "tasks" in Priority order, Done tasks under "doneTasks" (Import ignores them).
// "shown" = the tasks on the screen; "allTasks" = every task (for the summary numbers).
export function buildShownExport(shown, nowIso, allTasks) {
  const all = Array.isArray(allTasks) ? allTasks : shown;
  const names = SUMMARY_ROWS.map((r) => r[1]);
  const projects = [];
  shown.forEach((t) => { if (t.project && !projects.includes(t.project)) projects.push(t.project); });
  projects.sort((a, b) => { const ia = names.indexOf(a), ib = names.indexOf(b); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib); });
  const byPriority = (a, b) => priorityRank(a) - priorityRank(b) || (a.title || "").localeCompare(b.title || "");
  const open = shown.filter((t) => OPEN_STATUSES.includes(t.status)).sort(byPriority);
  const done = shown.filter((t) => t.status === "Done").sort(byPriority);
  const full = (t) => ({
    key: t.id, num: numLabel(t), title: t.title || "", project: t.project || "", type: t.type || "", status: t.status || "",
    release: t.release || "", priority: t.priority || "", targetDate: t.targetDate || "", doneWhen: t.doneWhen || "", link: t.link || "", note: t.note || "", details: t.details || "",
    history: Array.isArray(t.history) ? t.history : [], createdBy: t.createdBy || "",
    createdAt: isoOf(t.createdAt), updatedAt: isoOf(t.updatedAt), closedAt: isoOf(t.closedAt),
  });
  const lockFields = projects.length === 1 ? { onlyProject: projects[0] } : projects.length > 1 ? { onlyProjects: projects } : {};
  const parts = projects.map((p) => buildProjectExport(all, p, nowIso).summary);
  return {
    format: FORMAT,
    version: VERSION,
    exportedAt: nowIso,
    about: projects.length > 1 ? MULTI_EXPORT_ABOUT : PROJECT_EXPORT_ABOUT,
    ...lockFields,
    summaryTable: buildSummaryTable(all),
    summary: {
      ...(projects.length > 1 ? { projects: parts } : parts[0] || {}),
      release: "As shown on screen",
      pendingTasks: open.length,
      doneTasks: done.length,
      text:
        "Only the " + shown.length + " task(s) shown on the screen when this file was made (after the filters and the search)" +
        (projects.length ? ", from " + projects.join(" + ") : "") + ". " + open.length + " open task(s) are under tasks, in Priority order; " +
        done.length + " Done task(s) are listed separately under doneTasks; Import ignores them. The summary table counts all tasks.",
    },
    tasks: open.map(full),
    doneTasks: done.map(doneRow),
  };
}

// One task of any status. With "allTasks" it carries everything the full Export carries (lock to the task's
// project, summary, summary table, complete History, dates); the task is always under "tasks", even if it is Done.
// Without "allTasks" it gives the short old file.
export function buildSingleExport(task, nowIso, allTasks) {
  if (!Array.isArray(allTasks)) {
    return { format: FORMAT, version: VERSION, exportedAt: nowIso, about: ABOUT, tasks: [exportTask(task)] };
  }
  const f = buildShownExport([task], nowIso, allTasks);
  f.tasks = [{
    key: task.id, num: numLabel(task), title: task.title || "", project: task.project || "", type: task.type || "", status: task.status || "",
    release: task.release || "", priority: task.priority || "", targetDate: task.targetDate || "", doneWhen: task.doneWhen || "", link: task.link || "", note: task.note || "", details: task.details || "",
    history: Array.isArray(task.history) ? task.history : [], createdBy: task.createdBy || "",
    createdAt: isoOf(task.createdAt), updatedAt: isoOf(task.updatedAt), closedAt: isoOf(task.closedAt),
  }];
  f.doneTasks = [];
  f.summary.release = "One task";
  f.summary.pendingTasks = OPEN_STATUSES.includes(task.status) ? 1 : 0;
  f.summary.doneTasks = task.status === "Done" ? 1 : 0;
  f.summary.text =
    "Only ONE task (" + (numLabel(task) || task.id) + ") of " + (task.project || "no project") + ", with its complete History. " +
    "The summary of the project and the summary table (all tasks) are given for context.";
  return f;
}

// ---- Export of ONE project for our work sessions --------------------------------
// The Owner chooses the project first. The file holds only the pending work of that
// project (Pending, In progress, On hold), with the task numbers, the full History and
// a short summary. It carries a lock ("onlyProject") that Import checks.
const DAY_MS = 86400000;
const PROJECT_EXPORT_ABOUT =
  "Project Tracker file for ONE project (see onlyProject). To send changes back: use each task's key or its number (num, like T-094) " +
  "and change only the fields you want to change; a task without a key or number becomes a new task of this project; " +
  "addHistory is a list of short lines (up to 10000 characters each) added to the History; the import never deletes anything " +
  "and refuses any task that belongs to another project. createdAt, updatedAt, closedAt and history are for reading only and are ignored on import. " +
  "Never put a password, key or token in this file.";

// Time of a Firestore timestamp, an ISO text or a Date, as milliseconds (0 if none).
export function toMs(v) {
  try {
    if (!v) return 0;
    if (typeof v.toMillis === "function") return v.toMillis();
    if (typeof v === "string") { const m = Date.parse(v); return isNaN(m) ? 0 : m; }
    if (v.seconds) return v.seconds * 1000;
    if (v instanceof Date) return v.getTime();
  } catch (e) { /* ignore */ }
  return 0;
}
const isoOf = (v) => { const m = toMs(v); return m ? new Date(m).toISOString() : ""; };
const numLabel = (t) => (t.num ? "T-" + String(t.num).padStart(3, "0") : "");

// Priority order (same on screen and in Export):
// 1 open work, then On hold, then Done; 2 Release: Before pilot, untriaged, After pilot, Later;
// 3 Priority: High, Medium, not set, Low; 4 In progress, then Needs check, then To do; 5 Security before Normal; 6 oldest task number first.
export function priorityRank(t) {
  const grp = t.status === "Done" ? 2 : t.status === "Parked" ? 1 : 0;
  const rel = { "Before pilot": 0, "After pilot": 2, Later: 3 }[t.release] ?? 1;
  const pr = { High: 0, Medium: 1, Low: 3 }[t.priority] ?? 2;
  const st = t.status === "Doing" ? 0 : t.status === "Check" ? 1 : 2;
  const ty = t.type === "Security" ? 0 : 1;
  const n = typeof t.num === "number" ? t.num : parseNum(t.num);
  return grp * 1e10 + rel * 1e9 + pr * 1e8 + st * 1e7 + ty * 1e6 + (n || 999999);
}

// ---- Summary table (added 18 Oct 2026) --------------------------------------------
// One table for ALL tasks (open and Done), one row per project:
// Before release (= Before pilot) Done / Pending, After release (= After pilot) Done / Pending,
// Wish list (= Later), Not tasks (= no Release set), Total, and Security Done / Pending.
// Pending = To do, In progress and On hold. Security counts are part of the other columns.
export const SUMMARY_ROWS = [
  ["Main App", "Main app"],
  ["CRM", "CRM"],
  ["Invoice", "Billing app"],
  ["Website", "Website"],
  ["Common for all projects", "All projects"],
];
const SUMMARY_KEYS = ["beforeDone", "beforePending", "afterDone", "afterPending", "wishList", "notTasks", "total", "securityDone", "securityPending"];

export function buildSummaryTable(tasks) {
  const list = Array.isArray(tasks) ? tasks : [];
  const blank = () => SUMMARY_KEYS.reduce((o, k) => { o[k] = 0; return o; }, {});
  const addTo = (r, t) => {
    const done = t.status === "Done";
    if (t.release === "Before pilot") r[done ? "beforeDone" : "beforePending"] += 1;
    else if (t.release === "After pilot") r[done ? "afterDone" : "afterPending"] += 1;
    else if (t.release === "Later") r.wishList += 1;
    else r.notTasks += 1;
    r.total += 1;
    if (t.type === "Security") r[done ? "securityDone" : "securityPending"] += 1;
  };
  const known = SUMMARY_ROWS.map(([label, project]) => ({ project: label, name: project, ...blank() }));
  const other = { project: "Other projects", name: "", ...blank() };
  list.forEach((t) => {
    const row = known.find((r) => r.name === t.project);
    addTo(row || other, t);
  });
  const rows = other.total ? known.concat([other]) : known;
  const total = { project: "Total", ...blank() };
  rows.forEach((r) => SUMMARY_KEYS.forEach((k) => { total[k] += r[k]; }));
  const head = ["Project", "Before: Done", "Before: Pending", "After: Done", "After: Pending", "Wish list", "Not tasks", "Total", "Security: Done", "Security: Pending"];
  const line = (r) => [r.project, r.beforeDone, r.beforePending, r.afterDone, r.afterPending, r.wishList, r.notTasks, r.total, r.securityDone, r.securityPending].join(" | ");
  return {
    about:
      "All tasks, open and Done. Before = Before pilot, After = After pilot, Wish list = Later, Not tasks = no Release set. " +
      "Pending = To do, In progress, Needs check and On hold. Security counts are part of the other columns.",
    columns: head,
    rows: rows.map((r) => { const { name, ...rest } = r; return rest; }),
    total,
    text: [head.join(" | ")].concat(rows.map(line), [line(total)]).join("\n"),
  };
}

// A finished task as written in the Export (added 18 Oct 2026). Only the last 5 History lines, to keep the file small.
const doneRow = (t) => ({
  key: t.id,
  num: numLabel(t),
  title: t.title || "",
  project: t.project || "",
  type: t.type || "",
  status: t.status || "",
  release: t.release || "", priority: t.priority || "", targetDate: t.targetDate || "", doneWhen: t.doneWhen || "",
  link: t.link || "",
  note: t.note || "",
  details: t.details || "",
  history: (Array.isArray(t.history) ? t.history : []).slice(-5),
  createdBy: t.createdBy || "",
  createdAt: isoOf(t.createdAt),
  updatedAt: isoOf(t.updatedAt),
  closedAt: isoOf(t.closedAt),
});

export function buildProjectExport(tasks, project, nowIso, release, priority) {
  // release: "Before pilot", "After pilot", "Later" keeps only that release; "All" or nothing keeps every release
  const wantRelease = release && release !== "All" ? release : "";
  // priority (added 28 Oct 2026): "High", "Medium", "Low" or "Not set" keeps only that Priority; "All" or nothing keeps every Priority
  const wantPriority = priority && priority !== "All" ? priority : "";
  const prioFits = (t) => !wantPriority || (wantPriority === "Not set" ? !t.priority : t.priority === wantPriority);
  const now = Date.parse(nowIso);
  const mine = tasks.filter((t) => t.project === project);
  const closedMs = (t) => toMs(t.closedAt) || toMs(t.updatedAt);
  const open = mine.filter((t) => OPEN_STATUSES.includes(t.status) && (!wantRelease || (wantRelease === "Not set" ? !t.release : t.release === wantRelease)) && prioFits(t));
  const within7 = (ms) => ms > 0 && now - ms <= 7 * DAY_MS;
  const finished7 = mine.filter((t) => t.status === "Done" && within7(closedMs(t))).length;
  const created7 = mine.filter((t) => within7(toMs(t.createdAt))).length;
  const edited7 = mine.filter(
    (t) => within7(toMs(t.updatedAt)) && !within7(toMs(t.createdAt)) && !(t.status === "Done" && within7(closedMs(t)))
  ).length;
  const launch = mine.filter((t) => t.release === "Before pilot");
  const launchDone = launch.filter((t) => t.status === "Done").length;
  const list = open.slice().sort((a, b) => priorityRank(a) - priorityRank(b) || (a.title || "").localeCompare(b.title || ""));
  // The Done tasks of the same project and release. They go under "doneTasks", never under "tasks", so Import ignores them.
  const doneList = mine
    .filter((t) => t.status === "Done" && (!wantRelease || (wantRelease === "Not set" ? !t.release : t.release === wantRelease)) && prioFits(t))
    .sort((a, b) => priorityRank(a) - priorityRank(b) || (a.title || "").localeCompare(b.title || ""));
  return {
    format: FORMAT,
    version: VERSION,
    exportedAt: nowIso,
    about: PROJECT_EXPORT_ABOUT,
    onlyProject: project,
    summaryTable: buildSummaryTable(tasks),
    summary: {
      project,
      release: wantRelease || "All",
      priority: wantPriority || "All",
      pendingTasks: open.length,
      doneTasks: doneList.length,
      beforePilotTotal: launch.length,
      beforePilotFinished: launchDone,
      beforePilotLeft: launch.length - launchDone,
      last7Days: { finished: finished7, created: created7, edited: edited7 },
      text:
        project + " only. Launch blockers (Before pilot): " + launchDone + " done of " + launch.length + ", " + (launch.length - launchDone) + " left. " +
        "Last 7 days: " + finished7 + " finished, " + created7 + " new, " + edited7 + " edited. " +
        "This file has the " + open.length + " open task(s) of " + project + (wantRelease ? " marked " + wantRelease : "") + (wantPriority ? " with Priority " + wantPriority : "") + " (To do, In progress, Needs check, On hold), in Priority order. " + doneList.length + " Done task(s) are listed separately under doneTasks; Import ignores them.",
    },
    tasks: list.map((t) => ({
      key: t.id,
      num: numLabel(t),
      title: t.title || "",
      project: t.project || "",
      type: t.type || "",
      status: t.status || "",
      release: t.release || "", priority: t.priority || "", targetDate: t.targetDate || "", doneWhen: t.doneWhen || "",
      link: t.link || "",
      note: t.note || "",
      details: t.details || "",
      history: Array.isArray(t.history) ? t.history : [],
      createdBy: t.createdBy || "",
      createdAt: isoOf(t.createdAt),
      updatedAt: isoOf(t.updatedAt),
      closedAt: isoOf(t.closedAt),
    })),
    doneTasks: doneList.map(doneRow),
  };
}

// ---- Export of SEVERAL projects in ONE file (added 15 Oct 2026) ------------------
// The Owner ticks one or more projects. One project gives exactly the same file as before.
// Two or more give one file with a lock list ("onlyProjects"); Import then accepts only the
// ticked projects. Each task keeps its own project name, so nothing gets mixed up.
const MULTI_EXPORT_ABOUT =
  "Project Tracker file for SEVERAL projects (see onlyProjects). To send changes back: use each task's key or its number (num, like T-094) " +
  "and change only the fields you want to change; a new task must say its project, and that project must be one of onlyProjects; " +
  "addHistory is a list of short lines (up to 10000 characters each) added to the History; the import never deletes anything " +
  "and refuses any task that belongs to a project outside onlyProjects. createdAt, updatedAt, closedAt and history are for reading only and are ignored on import. " +
  "Never put a password, key or token in this file.";

export function buildProjectsExport(tasks, projects, nowIso, release, priority) {
  const list = [];
  (Array.isArray(projects) ? projects : []).forEach((p) => { if (typeof p === "string" && p && !list.includes(p)) list.push(p); });
  if (list.length === 0) return null;
  if (list.length === 1) return buildProjectExport(tasks, list[0], nowIso, release, priority);
  const parts = list.map((p) => buildProjectExport(tasks, p, nowIso, release, priority));
  const wantRelease = release && release !== "All" ? release : "";
  const wantPriority = priority && priority !== "All" ? priority : "";
  const total = parts.reduce((n, f) => n + f.tasks.length, 0);
  return {
    format: FORMAT,
    version: VERSION,
    exportedAt: nowIso,
    about: MULTI_EXPORT_ABOUT,
    onlyProjects: list,
    summaryTable: buildSummaryTable(tasks),
    summary: {
      projects: parts.map((f) => f.summary),
      release: wantRelease || "All",
      priority: wantPriority || "All",
      pendingTasks: total,
      text:
        list.join(" + ") + " together. This file has the " + total + " open task(s) of these " + list.length + " projects" +
        (wantRelease ? " marked " + wantRelease : "") + (wantPriority ? " with Priority " + wantPriority : "") + " (To do, In progress, Needs check, On hold), all projects together in Priority order. Their Done tasks are listed separately under doneTasks; Import ignores them. " +
        "Each project has its own summary under summary.projects.",
    },
    tasks: parts.reduce((all, f) => all.concat(f.tasks), []).sort((a, b) => priorityRank(a) - priorityRank(b)),
    doneTasks: parts.reduce((all, f) => all.concat(f.doneTasks), []).sort((a, b) => priorityRank(a) - priorityRank(b)),
  };
}

// "T-094", "t-94" or "94" -> 94 (0 if it is not a task number).
export function parseNum(v) {
  const m = /^\s*t?-?\s*0*(\d{1,6})\s*$/i.exec(String(v == null ? "" : v));
  return m ? Number(m[1]) : 0;
}

export function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Dry run: reads the file text and says what WOULD happen. Saves nothing.
export function planImport(text, tasks, lists) {
  const out = { fatal: "", rows: [], counts: { new: 0, update: 0, same: 0, problem: 0 }, warnings: 0 };
  const fail = (m) => { out.fatal = m; return out; };
  if (typeof text !== "string" || text.length === 0) return fail("The file is empty.");
  if (text.length > 2000000) return fail("The file is too big (over 2 MB).");
  let data;
  try { data = JSON.parse(text); } catch (e) { return fail("This is not a valid .json file."); }
  if (!data || typeof data !== "object" || data.format !== FORMAT)
    return fail('This is not a Project Tracker file (it should say format "' + FORMAT + '").');
  if (data.version !== VERSION) return fail("This file has version " + data.version + "; this screen reads version " + VERSION + ".");
  if (!Array.isArray(data.tasks) || data.tasks.length === 0) return fail("The file has no tasks.");
  if (data.tasks.length > 200) return fail("The file has more than 200 tasks. Please split it.");

  // The lock: the file says which project(s) it is for. Rows of other projects are refused.
  // "onlyProject" = one project (older files and single-project exports); "onlyProjects" = a list (several projects).
  let locks = [];
  const hasOne = data.onlyProject !== undefined && data.onlyProject !== null && data.onlyProject !== "";
  const hasMany = data.onlyProjects !== undefined && data.onlyProjects !== null;
  if (hasOne && hasMany) return fail("The file has both onlyProject and onlyProjects. Please keep only one of them.");
  if (hasOne) {
    if (typeof data.onlyProject !== "string" || !lists.projects.includes(data.onlyProject))
      return fail('The file is locked to a project that is not known ("' + String(data.onlyProject) + '"). Known projects: ' + lists.projects.join(", ") + ".");
    locks = [data.onlyProject];
  } else if (hasMany) {
    if (!Array.isArray(data.onlyProjects) || data.onlyProjects.length === 0 || data.onlyProjects.length > lists.projects.length)
      return fail("onlyProjects must be a list of project names (at least one).");
    for (const q of data.onlyProjects) {
      if (typeof q !== "string" || !lists.projects.includes(q))
        return fail('The file is locked to a project that is not known ("' + String(q) + '"). Known projects: ' + lists.projects.join(", ") + ".");
    }
    locks = data.onlyProjects.filter((q, i, a) => a.indexOf(q) === i);
  }
  const lock = locks.length === 1 ? locks[0] : ""; // the one project, when the file is for exactly one
  const lockText = locks.join(", ");
  out.lock = lock;
  out.locks = locks;

  const byId = new Map(tasks.map((t) => [t.id, t]));
  const byNum = new Map();
  tasks.forEach((t) => { const n = Number(t.num) || 0; if (n) byNum.set(n, t); });
  const seen = new Set();

  data.tasks.forEach((raw, i) => {
    const row = { n: i + 1, kind: "", title: "", key: "", fields: {}, adds: [], changes: [], problems: [], warnings: [], notes: [], closedAt: null };
    const done = () => { out.rows.push(row); out.counts[row.kind] += 1; out.warnings += row.warnings.length; };

    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      row.kind = "problem"; row.problems.push("This entry is not a task."); return done();
    }
    row.title = typeof raw.title === "string" ? raw.title : "";
    let key = typeof raw.key === "string" ? raw.key.trim() : "";
    let existing = null;
    if (raw.key !== undefined && raw.key !== null && raw.key !== "" && !key) row.problems.push("The key is not text.");

    // Task number (like T-094): finds the task when no key is given; must agree with the key when both are given.
    const rawNum = typeof raw.num === "string" ? raw.num.trim() : raw.num;
    let byNumber = null;
    if (rawNum !== undefined && rawNum !== null && rawNum !== "") {
      if (typeof rawNum !== "string") row.problems.push("The task number must be text, like T-094.");
      else {
        const n = parseNum(rawNum);
        if (!n) row.problems.push('The task number "' + rawNum + '" is not like T-094.');
        else {
          byNumber = byNum.get(n) || null;
          if (!byNumber) row.problems.push("The task number T-" + String(n).padStart(3, "0") + " is not in the tracker (nothing is created from an unknown number).");
          else row.num = "T-" + String(n).padStart(3, "0");
        }
      }
    }
    if (key && byNumber && byNumber.id !== key) row.problems.push("The key and the task number point to different tasks. Nothing is changed.");
    if (!key && byNumber) key = byNumber.id;

    if (key) {
      row.key = key;
      existing = byId.get(key) || null;
      if (!existing) row.problems.push("The key is not in the tracker (nothing is created from an unknown key).");
      if (seen.has(key)) row.problems.push("The same key appears twice in this file.");
      seen.add(key);
      if (existing && !row.title) row.title = existing.title || "";
      if (existing && !row.num && existing.num) row.num = "T-" + String(existing.num).padStart(3, "0");
    }

    // read and check the fields that were given
    const given = {};
    for (const f of FIELDS) {
      if (raw[f] === undefined) continue;
      const v = raw[f];
      if (typeof v !== "string") { row.problems.push(f + " must be text."); continue; }
      if (f === "project" && !lists.projects.includes(v)) { row.problems.push('project "' + v + '" is not one of: ' + lists.projects.join(", ") + "."); continue; }
      if (f === "type" && !lists.types.includes(v)) { row.problems.push('type "' + v + '" is not one of: ' + lists.types.join(", ") + "."); continue; }
      if (f === "status" && !lists.statuses.includes(v)) { row.problems.push('status "' + v + '" is not one of: ' + lists.statuses.join(", ") + "."); continue; }
      if (f === "release" && v.trim() !== "" && !(lists.releases || RELEASES).includes(v.trim())) { row.problems.push('release "' + v + '" is not one of: ' + (lists.releases || RELEASES).join(", ") + ", or empty."); continue; }
      if (f === "priority" && v.trim() !== "" && !(lists.priorities || PRIORITIES).includes(v.trim())) { row.problems.push('priority "' + v + '" is not one of: ' + (lists.priorities || PRIORITIES).join(", ") + ", or empty."); continue; }
      if (f === "targetDate" && v.trim() !== "" && !(/^\d{4}-\d{2}-\d{2}$/.test(v.trim()) && !isNaN(Date.parse(v.trim() + "T00:00:00Z")))) { row.problems.push('targetDate "' + v + '" must be a date like 2026-12-09, or empty.'); continue; }
      const val = f === "details" ? v : v.trim();
      if (f === "title" && !val) { row.problems.push("title is empty."); continue; }
      if (val.length > LIMITS[f] && LIMITS[f]) { row.problems.push(f + " is too long (over " + LIMITS[f] + " characters)."); continue; }
      given[f] = val;
      if (SECRET_WORDS.test(val)) row.warnings.push(f + " contains a word like password, token, secret or key.");
    }

    // History lines to add
    let lines = [];
    if (raw.addHistory !== undefined) {
      if (!Array.isArray(raw.addHistory)) row.problems.push("addHistory must be a list of lines.");
      else if (raw.addHistory.length > 20) row.problems.push("addHistory has more than 20 lines.");
      else {
        raw.addHistory.forEach((l) => {
          if (typeof l !== "string") { row.problems.push("An addHistory line is not text."); return; }
          const s = l.trim();
          if (!s) return;
          if (s.length > LIMITS.line) { row.problems.push("An addHistory line is too long (over " + LIMITS.line + " characters)."); return; }
          if (SECRET_WORDS.test(s)) row.warnings.push("addHistory contains a word like password, token, secret or key.");
          lines.push(s);
        });
      }
    }

    // The lock: the project this row would end up in must be one of the projects of the file.
    if (locks.length) {
      const target = given.project !== undefined ? given.project : existing ? existing.project : lock;
      if (!target) row.problems.push("This file is for several projects (" + lockText + "), so a new task must say its project. It is skipped.");
      else if (!locks.includes(target)) row.problems.push("This file is only for " + lockText + ', but this task is for "' + target + '". It is skipped.');
      if (existing && !locks.includes(existing.project)) row.problems.push('The task found is in "' + (existing.project || "no project") + '", not in ' + lockText + ". It is skipped.");
    }

    const extra = Object.keys(raw).filter((k) => !FIELDS.includes(k) && !IGNORED.includes(k));
    if (extra.length) row.notes.push("Ignored: " + extra.join(", "));

    if (row.problems.length) { row.kind = "problem"; return done(); }

    if (!key) {
      // a new task
      if (!given.title) { row.kind = "problem"; row.problems.push("A new task needs a title."); return done(); }
      row.fields = {
        title: given.title, project: given.project || lock || "Main app", type: given.type || "Normal",
        status: given.status || "Pending", release: given.release || "", priority: given.priority || "", targetDate: given.targetDate || "", doneWhen: given.doneWhen || "", link: given.link || "", note: given.note || "", details: given.details || "",
      };
      row.adds = lines;
      row.kind = "new";
      return done();
    }

    // an update of an existing task: only what really differs
    for (const f of FIELDS) {
      if (given[f] === undefined) continue;
      const old = existing[f] === undefined || existing[f] === null ? "" : existing[f];
      if (given[f] !== old) {
        row.fields[f] = given[f];
        if (f === "status") row.changes.push("status " + (old ? show(old) : "-") + " → " + show(given[f]));
        else if (f === "details") row.changes.push("details (text replaced)");
        else row.changes.push(f);
      }
    }
    if (row.fields.status !== undefined) row.adds.push("Status: " + (existing.status ? show(existing.status) : "-") + " → " + show(row.fields.status));
    if (row.fields.release !== undefined) row.adds.push("Release: " + (existing.release || "-") + " → " + (row.fields.release || "-"));
    if (row.fields.priority !== undefined) row.adds.push("Priority: " + (existing.priority || "-") + " → " + (row.fields.priority || "-"));
    if (row.fields.targetDate !== undefined) row.adds.push("Target date: " + (existing.targetDate || "-") + " → " + (row.fields.targetDate || "-"));
    if (row.fields.doneWhen !== undefined) row.adds.push("Done when: " + (existing.doneWhen ? "changed" : "set"));
    row.adds = row.adds.concat(lines);
    row.closedAt = existing.closedAt || null;
    if (lines.length) row.changes.push(lines.length + " history line(s)");
    row.kind = row.changes.length || row.adds.length ? "update" : "same";
    done();
  });
  return out;
}